import React from "react";
import { Repeat, Globe, SkipForward, Share2 } from "lucide-react";
import { REGION_TABS } from "../../lib/regions";

// FeedPlayerControlsBar — the segmented-pill controls row that sits
// directly under the player. Extracted from FeedPlayerOverlay.jsx in
// Iter-Feb 2026 to keep that file under control. Pure prop-driven —
// owns no state.
//
// Layout (left→right):
//   [Mode pill: All | Loop]
//   [Source pill: Global | Following]      (logged-in only)
//   [Region cycler — single chip on mobile, full segmented group on lg+]
//   ---- spacer ----
//   [Position dots / counter] [Next] [Share]
const FeedPlayerControlsBar = ({
  user,
  isLoopMode,
  setIsLoopMode,
  source,
  setSource,
  activeRegion,
  setActiveRegion,
  setCurrentVideoIndex,
  activeVideos,
  currentVideoIndex,
  handleSkipNext,
  handleShare,
  // 'home' (default) — show source + region selectors.
  // 'profile' / 'following' — hide them; the parent already constrained
  // the video set so those toggles are meaningless. Keeps the controls
  // row consistent across all three player surfaces.
  mode = 'home',
}) => {
  const showFeedSelectors = mode === 'home';
  return (
    <div className="w-full max-w-4xl mt-3 flex items-center justify-between gap-1.5 flex-nowrap overflow-x-auto no-scrollbar">
      <div className="flex items-center gap-1.5 flex-nowrap min-w-0 shrink-0">
        {/* Mode pill — All / Loop */}
        <div className="inline-flex items-center bg-gray-900/80 border border-gray-800 rounded-full p-0.5 shrink-0" data-testid="player-mode-pill">
          <button
            type="button"
            onClick={() => setIsLoopMode(false)}
            className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${!isLoopMode ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
            data-testid="player-mode-all-btn"
            aria-pressed={!isLoopMode}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setIsLoopMode(true)}
            className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${isLoopMode ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
            data-testid="player-mode-loop-btn"
            aria-pressed={isLoopMode}
            aria-label="Loop"
          >
            <Repeat className="h-3 w-3" />
          </button>
        </div>

        {/* Feed source pill — Global / Following (logged-in only, home mode only) */}
        {user && showFeedSelectors && (
          <div className="inline-flex items-center bg-gray-900/80 border border-gray-800 rounded-full p-0.5 shrink-0" data-testid="player-source-pill">
            <button
              type="button"
              onClick={() => { setSource('all'); setCurrentVideoIndex(0); }}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${source === 'all' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
              data-testid="player-source-global-btn"
              aria-pressed={source === 'all'}
            >
              Global
            </button>
            <button
              type="button"
              onClick={() => { setSource('following'); setCurrentVideoIndex(0); }}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${source === 'following' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
              data-testid="player-source-following-btn"
              aria-pressed={source === 'following'}
            >
              Following
            </button>
          </div>
        )}

        {/* Region cycler — only on home (the Global/Americas/Asia/etc
            filter is meaningless on a single user's profile or on a
            following-only feed). */}
        {showFeedSelectors && (
          <>
            <button
              type="button"
              onClick={() => {
                const idx = REGION_TABS.indexOf(activeRegion);
                const next = REGION_TABS[(idx + 1) % REGION_TABS.length];
                setActiveRegion(next);
              }}
              className="lg:hidden inline-flex items-center gap-1 bg-gray-900/80 border border-gray-800 hover:border-emerald-500/40 rounded-full px-2.5 py-1 text-[11px] font-medium text-emerald-300 transition-colors shrink-0"
              data-testid="player-region-cycle-btn"
              aria-label={`Region: ${activeRegion} — tap to cycle`}
            >
              <Globe className="h-3 w-3" /> {activeRegion}
            </button>
            <div className="hidden lg:inline-flex items-center bg-gray-900/80 border border-gray-800 rounded-full p-0.5 shrink-0" data-testid="player-region-pill">
              {REGION_TABS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setActiveRegion(r)}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${activeRegion === r ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  data-testid={`player-region-${r.toLowerCase()}-btn`}
                  aria-pressed={activeRegion === r}
                >
                  {r}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {activeVideos.length > 0 && activeVideos.length <= 8 && (
          <div className="hidden sm:flex gap-1">
            {activeVideos.map((v, idx) => (
              <button
                key={v.id || idx}
                onClick={() => setCurrentVideoIndex(idx)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentVideoIndex ? 'bg-emerald-500 w-4' : 'bg-gray-600 hover:bg-gray-500'}`}
                data-testid={`player-dot-${idx}`}
              />
            ))}
          </div>
        )}
        {activeVideos.length > 8 && (
          <span className="text-gray-400 text-[10px] tabular-nums" data-testid="player-position-counter">
            {currentVideoIndex + 1}/{activeVideos.length}
          </span>
        )}
        {/* Next — icon only so the row stays on one line on narrow
            viewports. Tooltip + aria-label preserve UX. */}
        <button
          onClick={handleSkipNext}
          className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-all shrink-0"
          data-testid="player-next-btn"
          aria-label="Next video"
          title="Next video"
        >
          <SkipForward className="h-3.5 w-3.5" />
        </button>
        {/* Share — icon only on mobile, full on desktop. */}
        <button
          onClick={handleShare}
          className="flex items-center justify-center w-7 h-7 sm:w-auto sm:h-auto sm:gap-1 sm:px-2.5 sm:py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-medium transition-all shrink-0"
          data-testid="player-share-btn"
          aria-label="Share video"
          title="Share video"
        >
          <Share2 className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>
    </div>
  );
};

export default FeedPlayerControlsBar;
